import { Component, EventEmitter, Input, Output } from "@angular/core";
import {
  Events,
  NavController,
  NavParams,
  ViewController,
} from "ionic-angular";
import {
  DropdownItem,
  Order,
  OrderComponent,
} from "models/order-management/models";
import { OrderFormState } from "pages/orders-entry-om/add-order/order-form-state";
import { StateManagementService } from "../../../providers/state-management/state-management.service";
import {
  OrderComponentInterface,
  OrderComponentPropertyUpdate,
  OrderCreateRequestUpdate,
} from "../order-components-interface";

export const LOCATION_TYPE_HOME = "home";

@Component({
  selector: "care-location-type",
  templateUrl: "care-location-type.html",
})
export class CareLocationTypeComponent implements OrderComponentInterface {
  /****************************************************************************
   * Inputs                                                                   *
   ****************************************************************************/
  @Input() orderComponent: OrderComponent;
  @Input() orderCreateRequest: Order;
  @Input() set orderFormState(orderFormState: OrderFormState) {
    if (
      this._orderFormState &&
      this._orderFormState.levelOfCare === orderFormState.levelOfCare
    ) {
      return;
    }
    this._orderFormState = orderFormState;
    this.levelOfCare = orderFormState.levelOfCare;
    this.careLocationDropdownItems = this.orderComponent.properties
      .find((property) => property.name === "careLocationType")
      .dropdownItems.filter((location) =>
        location.filterValues.includes(this.levelOfCare)
      );
    this.selectedCareLocation = null;
    this.checkIsValid();
  }

  /****************************************************************************
   * Outputs                                                                  *
   ****************************************************************************/
  @Output() orderCreateRequestEvent =
    new EventEmitter<OrderCreateRequestUpdate>();
  @Output() orderFormStateEvent = new EventEmitter<OrderFormState>();
  @Output() isValidEvent = new EventEmitter<boolean>();
  careLocationDropdownItems: DropdownItem[] = [];
  @Output() amendedOrderRequestEvent =
    new EventEmitter<OrderComponentPropertyUpdate>();

  /****************************************************************************
   * State                                                                    *
   ****************************************************************************/
  levelOfCare: string;
  _orderFormState: OrderFormState;
  selectedCareLocation: string;
  careLocationTypeId: number;

  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    public viewCtrl: ViewController,
    public events: Events,
    public stateData: StateManagementService
  ) {}

  /****************************************************************************
   * Lifecycle Methods                                                        *
   ****************************************************************************/
  ngOnInit() {
    this._orderFormState.formType === "amendment"
      ? this.setAmendedValue()
      : null;
    this.checkIsValid();
  }

  /****************************************************************************
   * Setter Methods                                                           *
   ****************************************************************************/
  setAmendedValue() {
    const componentField: string = this.orderComponent.properties[0].name;
    this.selectedCareLocation = this.orderCreateRequest[componentField];
    const matchingItem = this.careLocationDropdownItems.find(
      (item) => item.label === this.selectedCareLocation
    );
    this.orderFormStateEvent.emit({
      careLocationType: matchingItem.value,
      careLocation: null,
    });
    this.checkIsValid();
  }

  /****************************************************************************
   * Component Related Methods                                                *
   ****************************************************************************/
  onCareLocationTypeChange(event: DropdownItem) {
    if (event) {
      this.careLocationTypeId = isNaN(parseInt(event.value))
        ? null
        : parseInt(event.value);
      if (
        this.orderComponent.properties[0].amendable &&
        this._orderFormState.formType === "amendment"
      ) {
        const componentField = this.orderComponent.properties[0].name;
        const prevValue = this.orderCreateRequest[componentField].toString();
        if (prevValue !== event.label) {
          const amendedObject: OrderComponentPropertyUpdate = { updates: {} };
          amendedObject.updates = {
            propertyName: componentField,
            previousValueDisplay: this.orderCreateRequest[componentField],
            previousValue: this.orderCreateRequest[componentField],
            newValue: event.label,
          };
          if (event.value === LOCATION_TYPE_HOME) {
            this.orderCreateRequestEvent.emit({
              deletes: ["careLocationTypeId"],
            });
          } else {
            this.amendedOrderRequestEvent.emit(amendedObject);
            const prevLocationTypeId = this.orderCreateRequest
              .careLocationTypeId
              ? this.orderCreateRequest.careLocationTypeId.toString()
              : "0";
            amendedObject.updates = {
              propertyName: "careLocationTypeId",
              previousValue: prevLocationTypeId,
              previousValueDisplay: prevLocationTypeId,
              newValue: event.value,
            };
            this.amendedOrderRequestEvent.emit(amendedObject);
          }
        } else {
          const amendedObject: OrderComponentPropertyUpdate = {
            updates: {},
            deletes: [componentField as keyof Order],
          };
          this.amendedOrderRequestEvent.emit(amendedObject);
        }
      } else {
        this.orderCreateRequestEvent.emit({
          updates: {
            careLocationTypeId: this.careLocationTypeId,
            careLocationType: event.label,
          },
          deletes: this.careLocationTypeId ? [] : ["careLocationTypeId"],
        });
      }
      this.orderFormStateEvent.emit({
        careLocationType: event.value,
        careLocation: null,
      });
    }
    this.checkIsValid();
  }

  checkIsValid() {
    if (
      this.orderComponent.properties[0].required &&
      (!this.selectedCareLocation || this.selectedCareLocation.length === 0)
    ) {
      this.isValidEvent.emit(false);
    } else {
      this.isValidEvent.emit(true);
    }
  }

  /****************************************************************************
   * Cypress-Related ("data-cy") Methods                                      *
   ****************************************************************************/
  toKebabCase(label: string): string {
    if (label) {
      return label
        .replace(/^\s+|\s+$/gm, "")
        .replace(/([a-z])([A-Z])/g, "$1-$2")
        .replace(/[\s_]+/g, "-")
        .toLowerCase();
    }
    return "";
  }

  createDataCyAttribute(component: string, option: string): string {
    return `${this.toKebabCase(component)}-option-${this.toKebabCase(option)}`;
  }
}



// WEBPACK FOOTER //
// ./src/components/order-management/care-location-type/care-location-type.ts